/**
 * @desc: CasualGamesHub/RobloxTopCharts 页面
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classnames from 'classnames';
import { useIntl, useLocation } from 'umi';
import { Card, Justify } from 'tea-component';
import '@intelligence/v3/filterControls';
import Filter from '@intelligence/v3/components/Filter/index';
import CardSlot from '@intelligence/components/CardSlot';
import SearchRecent from '@intelligence/components/SearchRecent';
import ReusePageTemplate from '@intelligence/v3/components/ReusePageTemplate';
import RobloxPageTitle from '@intelligence/v3/components/RobloxPageTitle';
import useFilterablePage from '@intelligence/v3/_hooks/useFilterablePage';
import useRobloxFilterablePageBoot from '@intelligence/v3/_hooks/useRobloxFilterablePageBoot';
import useMarketFilterApi, { MarketSpecailType } from '@intelligence/hooks/useMarketSelector';
import useGenreSourcesOptions from '@intelligence/components/GenreSelectMiddleware/_hooks/useGenreSourcesOptions';
import { GENRE_SOURCE_MAP } from '@intelligence/components/GenreSelect/constants';
import { fetchRobloxTopChartsMetrics } from '@intelligence/v3/services/robloxTopCharts';
import RobloxTopChartsTable from './components/RobloxTopChartsTable';
import RobloxTopChartsSectionTitle from './components/RobloxTopChartsSectionTitle';
import RobloxTopChartsLineChart from './components/RobloxTopChartsLineChart';
import RobloxTopChartsDownload from './components/RobloxTopChartsDownload';
import useRobloxTopChartsTable from './hooks/useRobloxTopChartsTable';
import useRobloxTopChartsChart from './hooks/useRobloxTopChartsChart';
import useRobloxTopChartsDefaultDate from './hooks/useRobloxTopChartsDefaultDate';
import usePageLog from '@intelligence/v3/_hooks/usePageLog';
import {
  getRobloxTopChartsSearchPageLogInfo,
  ROBLOX_TOP_CHARTS_PAGE_LOG_KEY,
} from './config/pageLogConfig';
import {
  FILTER_SAVE_TYPE,
  PAGE_ID,
  ROBLOX_TOP_CHARTS_SEARCH_DROPDOWN_PAGE_SIZE,
  getDefaultFilterValues,
} from './constants';
import {
  getRobloxTopChartsChartMetricFromSortKey,
  getRobloxTopChartsChartTitle,
  ROBLOX_TOP_CHARTS_DEFAULT_SORT_KEY,
} from './config/robloxTopChartsSort';
import { getRobloxTopChartsFilterItems } from './filterConfig';
import { buildRobloxTopChartsFetchParams } from './utils/buildRobloxTopChartsFetchParams';
import { transRobloxTopChartsTableDataForSearch } from './utils/transRobloxTopChartsSearchData';
import { getRobloxTopChartsFilterDateFromComponentDefault } from './utils/robloxTopChartsDefaultDate';
import { getRangePickerValue } from '@intelligence/v3/filterControls/rangePicker/utils';
import {
  clearRobloxTopChartsDrillDownGameIdFromUrl,
  parseRobloxTopChartsDrillDownGameId,
  parseRobloxTopChartsDrillDownGameName,
} from '@intelligence/v3/pages/RobloxGameRank/utils/robloxHubNavigation';
import type { IRobloxTopChartsFilterValues } from './types';
import type { IMainGenre } from '@intelligence/types/common';
import type { SortByItemType } from '@intelligence/components/TableComponents/components/SortBy';
import type { TableSelectionType, TableSelectStatusType } from '@intelligence/components/TableComponents/components/TableCheckbox';
import './index.scss';

interface IRobloxTopChartsProps extends IBaseProps {}

const RobloxTopCharts: React.FC<IRobloxTopChartsProps> = (props) => {
  const { style, className } = props;
  const { formatMessage } = useIntl();
  const location = useLocation();
  const filterValuesRef = useRef<IRobloxTopChartsFilterValues>(getDefaultFilterValues());
  const robloxGenreOptionsRef = useRef<IMainGenre[]>([]);
  const prevDrillDownGameIdRef = useRef<string>('');

  const defaultValues = useMemo(() => getDefaultFilterValues(), []);
  const { filterLoading, regionOptions } = useMarketFilterApi({
    filterType: MarketSpecailType.common,
  });
  const { genres, options: genreOptions, loading: genreOptionsLoading } = useGenreSourcesOptions({
    sources: [GENRE_SOURCE_MAP.ROBLOX],
  });
  robloxGenreOptionsRef.current = genres.roblox || [];
  const [filterValues, setFilterValues] = useState<IRobloxTopChartsFilterValues>(defaultValues);
  const [isCoupledDataLoading, setIsCoupledDataLoading] = useState(false);

  usePageLog(ROBLOX_TOP_CHARTS_PAGE_LOG_KEY);

  const searchPageLogInfo = useMemo(() => getRobloxTopChartsSearchPageLogInfo(), []);

  const drillDownGameId = useMemo(
    () => parseRobloxTopChartsDrillDownGameId(location.search),
    [location.search],
  );

  const drillDownGameName = useMemo(
    () => parseRobloxTopChartsDrillDownGameName(location.search),
    [location.search],
  );

  const optionsReady = !filterLoading && !genreOptionsLoading;
  const {
    loading: defaultDateLoading,
    componentDefault: rangePickerComponentDefault,
  } = useRobloxTopChartsDefaultDate(optionsReady);

  const items = useMemo(() => {
    return getRobloxTopChartsFilterItems({
      regionOptions: regionOptions || [],
      genreOptions: { roblox: genreOptions.roblox || [] },
      genreOptionsLoading,
      rangePickerComponentDefault,
    });
  }, [regionOptions, genreOptions.roblox, genreOptionsLoading, rangePickerComponentDefault]);

  const hookReady = optionsReady && !defaultDateLoading;
  const { getInitialFilterValues, syncFilterState } = useFilterablePage({
    pageId: PAGE_ID,
    items,
    defaultValues,
    ready: hookReady,
  });

  const {
    loading: tableLoading,
    records,
    total,
    pageIndex,
    pageSize,
    selectedGameId,
    tableSelection,
    tableSelectionAll,
    sortKey,
    sortOrder,
    fetchTable,
    handlePagingChange,
    handleCheckGame,
    handleSortChange,
  } = useRobloxTopChartsTable(robloxGenreOptionsRef);

  const {
    loading: chartLoading,
    chartList,
    fetchChart,
    clearChart,
    setChartLoading,
  } = useRobloxTopChartsChart(robloxGenreOptionsRef);

  const selectedGameSearchLabel = useMemo(() => {
    if (!selectedGameId) {
      return '';
    }

    if (drillDownGameName && drillDownGameId === selectedGameId) {
      return drillDownGameName;
    }

    const matchedRecord = records.find(
      (item) => String(item?.game_id || '') === selectedGameId,
    );

    return matchedRecord?.entity_name || drillDownGameName || '';
  }, [selectedGameId, drillDownGameId, drillDownGameName, records]);

  const resetValues = useMemo(() => {
    const defaultDate = getRobloxTopChartsFilterDateFromComponentDefault(
      defaultValues,
      rangePickerComponentDefault,
    );

    if (!defaultDate) {
      return defaultValues;
    }

    return {
      ...defaultValues,
      date: defaultDate,
    };
  }, [defaultValues, rangePickerComponentDefault]);

  const mergeFilterWithDefaultDate = useCallback((
    filterValues: IRobloxTopChartsFilterValues,
  ): IRobloxTopChartsFilterValues => {
    const defaultDate = getRobloxTopChartsFilterDateFromComponentDefault(
      filterValues,
      rangePickerComponentDefault,
    );

    if (!defaultDate) {
      return filterValues;
    }

    return {
      ...filterValues,
      date: defaultDate,
    };
  }, [rangePickerComponentDefault]);

  const handleChartFetch = useCallback(async (
    filterValues: IRobloxTopChartsFilterValues,
    payload: {
      chartGameIds: string[];
      sortKey: string;
      sortOrder: 'asc' | 'desc';
    },
  ) => {
    if (payload.chartGameIds.length) {
      await fetchChart(filterValues, {
        gameIds: payload.chartGameIds,
        sortKey: payload.sortKey,
        sortOrder: payload.sortOrder,
        chartMetric: getRobloxTopChartsChartMetricFromSortKey(payload.sortKey),
      });
      return;
    }
    clearChart();
  }, [fetchChart, clearChart]);

  const fetchTableWithChart = useCallback(async (
    filterValues: IRobloxTopChartsFilterValues,
    options?: Parameters<typeof fetchTable>[1],
  ) => {
    setIsCoupledDataLoading(true);
    setChartLoading(true);

    try {
      await fetchTable(filterValues, {
        ...options,
        fetchChart: true,
        onAfterFetch: (payload) => handleChartFetch(filterValues, payload),
      });
    } finally {
      setIsCoupledDataLoading(false);
    }
  }, [fetchTable, handleChartFetch, setChartLoading]);

  const { isPageBootLoading, loading } = useRobloxFilterablePageBoot({
    hookReady,
    filterLoading,
    locationSearch: location.search,
    locationKey: location.key,
    getInitialFilterValues,
    logTag: 'RobloxTopCharts',
    onBeforeInitialize: () => {
      prevDrillDownGameIdRef.current = '';
    },
    onInitialized: async (nextFilterValues) => {
      const mergedFilterValues = mergeFilterWithDefaultDate(nextFilterValues);
      setFilterValues(mergedFilterValues);
      filterValuesRef.current = mergedFilterValues;

      const currentDrillDownGameId = parseRobloxTopChartsDrillDownGameId(location.search);
      prevDrillDownGameIdRef.current = currentDrillDownGameId;
      await fetchTableWithChart(mergedFilterValues, {
        resetPage: true,
        resetSelection: true,
        ...(currentDrillDownGameId
          ? { rankGameIds: [currentDrillDownGameId] }
          : { gameIds: [] }),
      });
      await syncFilterState(mergedFilterValues);
    },
  });

  const searchFetchParams = useMemo(() => {
    return buildRobloxTopChartsFetchParams(filterValues, {
      page: 1,
      pageSize: ROBLOX_TOP_CHARTS_SEARCH_DROPDOWN_PAGE_SIZE,
      robloxGenreOptions: robloxGenreOptionsRef.current,
    });
  }, [filterValues, genreOptionsLoading]);

  const searchListApi = useCallback((params: any, options?: { signal?: AbortSignal }) => {
    return fetchRobloxTopChartsMetrics(params, options);
  }, []);

  const getTableSearchList = useCallback((gameId: string) => {
    fetchTableWithChart(filterValuesRef.current, {
      rankGameIds: [gameId],
      resetPage: true,
      resetSelection: true,
    });
  }, [fetchTableWithChart]);

  const exitDrillDownMode = useCallback(() => {
    clearRobloxTopChartsDrillDownGameIdFromUrl();
    prevDrillDownGameIdRef.current = '';
  }, []);

  const handleSearchClear = useCallback(() => {
    exitDrillDownMode();
    fetchTableWithChart(filterValuesRef.current, {
      gameIds: [],
      resetPage: true,
      resetSelection: true,
    });
  }, [fetchTableWithChart, exitDrillDownMode]);

  const chartGranularity = useMemo(
    () => getRangePickerValue(filterValues.date ?? getDefaultFilterValues().date).granularity || 'daily',
    [filterValues.date],
  );

  const chartTitle = useMemo(
    () => getRobloxTopChartsChartTitle(sortKey, formatMessage),
    [sortKey, formatMessage],
  );

  const chartMetric = useMemo(
    () => getRobloxTopChartsChartMetricFromSortKey(sortKey),
    [sortKey],
  );

  const handleTableSortChange = useCallback(async (sortItem: SortByItemType) => {
    const nextSortKey = sortItem?.key || ROBLOX_TOP_CHARTS_DEFAULT_SORT_KEY;
    const nextSortOrder = sortItem?.sortVal === 'asc' ? 'asc' : 'desc';

    exitDrillDownMode();
    setChartLoading(true);

    try {
      const { chartGameIds } = await handleSortChange(
        filterValuesRef.current,
        sortItem,
      );

      if (chartGameIds.length) {
        await fetchChart(filterValuesRef.current, {
          gameIds: chartGameIds,
          sortKey: nextSortKey,
          sortOrder: nextSortOrder,
          chartMetric: getRobloxTopChartsChartMetricFromSortKey(nextSortKey),
        });
        return;
      }

      clearChart();
    } catch (error) {
      console.debug('[RobloxTopCharts] sort change failed', error);
    }
  }, [handleSortChange, fetchChart, clearChart, setChartLoading, exitDrillDownMode]);

  const handleTableCheckGame = useCallback((
    list: TableSelectionType[],
    status: TableSelectStatusType,
  ) => {
    handleCheckGame(list, status);
    const gameIds = list
      .map((item) => String(item?.gameId || ''))
      .filter(Boolean);
    if (gameIds.length) {
      fetchChart(filterValuesRef.current, {
        gameIds,
        sortKey,
        sortOrder,
        chartMetric,
      });
      return;
    }
    clearChart();
  }, [handleCheckGame, fetchChart, clearChart, sortKey, sortOrder, chartMetric]);

  useEffect(() => {
    filterValuesRef.current = filterValues;
  }, [filterValues]);

  useEffect(() => {
    if (isPageBootLoading) {
      return;
    }

    if (drillDownGameId === prevDrillDownGameIdRef.current) {
      return;
    }

    prevDrillDownGameIdRef.current = drillDownGameId;

    if (!drillDownGameId) {
      return;
    }

    fetchTableWithChart(filterValuesRef.current, {
      rankGameIds: [drillDownGameId],
      resetPage: true,
      resetSelection: true,
    });
  }, [drillDownGameId, isPageBootLoading, fetchTableWithChart]);

  const isTableDataPending = tableLoading || isCoupledDataLoading || isPageBootLoading;
  const tableDisplayRecords = isTableDataPending ? [] : records;

  const handleConfirm = (values: IRobloxTopChartsFilterValues): void => {
    exitDrillDownMode();
    setFilterValues(values);
    filterValuesRef.current = values;
    syncFilterState(values);
    fetchTableWithChart(values, {
      resetPage: true,
      resetSelection: true,
      gameIds: [],
    });
  };

  const handleReset = (values: IRobloxTopChartsFilterValues): void => {
    exitDrillDownMode();
    setFilterValues(values);
    filterValuesRef.current = values;
    fetchTableWithChart(values, {
      resetPage: true,
      resetSelection: true,
      gameIds: [],
    });
  };

  return (
    <ReusePageTemplate
      style={style}
      className={classnames('dbi-reuse-page-roblox-top-charts', className)}
      title={
        <RobloxPageTitle
          title={formatMessage({ id: 'dbi_reuse_page_title_roblox_top_charts' })}
        />
      }
    >
      <div className="dbi-multi-datasource-top-charts-template">
        <Card>
          <Card.Body>
            <Filter
              loading={loading}
              items={items}
              value={filterValues}
              resetValue={resetValues}
              saveFilterType={FILTER_SAVE_TYPE}
              useActionBar
              useSaveFilter
              useSticky
              stickyPortalContainer=".dbi-multi-datasource-top-charts-template"
              onConfirm={handleConfirm}
              onReset={handleReset}
            />
          </Card.Body>
        </Card>

        <RobloxTopChartsSectionTitle
          sortKey={sortKey}
          sortOrder={sortOrder}
          chart={
            <RobloxTopChartsLineChart
              loading={chartLoading || isPageBootLoading}
              chartList={chartList}
              tableSelection={tableSelection}
              chartMetric={chartMetric}
              chartTitle={chartTitle}
              granularity={chartGranularity}
            />
          }
        />

        <Justify
          className="dbi-card-justify"
          right={
            <>
              <SearchRecent
                pageLogInfo={searchPageLogInfo}
                searchType="roblox"
                searchKey="keyword"
                fetchParams={searchFetchParams}
                initSearchInput={!selectedGameId}
                searchDisplayValue={selectedGameSearchLabel}
                placeholder={formatMessage({ id: 'dbi_search_any_game' })}
                searchListApi={searchListApi}
                getTableResultList={getTableSearchList}
                transTableDataForSearch={transRobloxTopChartsTableDataForSearch}
                handleClear={handleSearchClear}
              />
              <RobloxTopChartsDownload
                filterValues={filterValues}
                tableSelection={tableSelection}
                regionOptions={regionOptions || []}
                total={total}
                robloxGenreOptions={robloxGenreOptionsRef.current}
                sortKey={sortKey}
                sortOrder={sortOrder}
              />
            </>
          }
        />

        <CardSlot>
          <RobloxTopChartsTable
            loading={tableLoading || isTableDataPending}
            records={tableDisplayRecords}
            total={total}
            pageIndex={pageIndex}
            pageSize={pageSize}
            tableSelection={tableSelection}
            tableSelectionAll={tableSelectionAll}
            sortKey={sortKey}
            sortOrder={sortOrder}
            onPagingChange={(paging) => {
              handlePagingChange(filterValuesRef.current, paging);
            }}
            onCheckGame={handleTableCheckGame}
            onSortChange={handleTableSortChange}
            robloxGenreOptions={genres.roblox || []}
          />
        </CardSlot>
      </div>
    </ReusePageTemplate>
  );
};

export default RobloxTopCharts;
